'use client';

import Script from 'next/script';

/**
 * Microsoft Clarity Tracking
 */
interface MicrosoftClarityProps {
  clarityId: string;
}

export function MicrosoftClarity({ clarityId }: MicrosoftClarityProps) {
  if (!clarityId) return null;

  return (
    <Script
      id='microsoft-clarity'
      strategy='afterInteractive'
      dangerouslySetInnerHTML={{
        __html: `
          (function(c,l,a,r,i,t,y){
            c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
            t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
            y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${clarityId}");
        `,
      }}
    />
  );
}

/**
 * Hotjar Tracking
 */
interface HotjarProps {
  hotjarId: string;
  hotjarVersion?: number;
}

export function Hotjar({ hotjarId, hotjarVersion = 6 }: HotjarProps) {
  if (!hotjarId) return null;

  return (
    <Script
      id='hotjar'
      strategy='afterInteractive'
      dangerouslySetInnerHTML={{
        __html: `
          (function(h,o,t,j,a,r){
            h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
            h._hjSettings={hjid:${hotjarId},hjsv:${hotjarVersion}};
            a=o.getElementsByTagName('head')[0];
            r=o.createElement('script');r.async=1;
            r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
            a.appendChild(r);
          })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
        `,
      }}
    />
  );
}

/**
 * Facebook Pixel
 */
interface FacebookPixelProps {
  pixelId: string;
}

export function FacebookPixel({ pixelId }: FacebookPixelProps) {
  if (!pixelId) return null;

  return (
    <>
      <Script
        id='facebook-pixel'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${pixelId}');
            fbq('track', 'PageView');
          `,
        }}
      />
      <noscript>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          height='1'
          width='1'
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`}
          alt=''
        />
      </noscript>
    </>
  );
}

/**
 * LinkedIn Insight Tag
 */
interface LinkedInInsightProps {
  partnerId: string;
}

export function LinkedInInsight({ partnerId }: LinkedInInsightProps) {
  if (!partnerId) return null;

  return (
    <Script
      id='linkedin-insight'
      strategy='afterInteractive'
      dangerouslySetInnerHTML={{
        __html: `
          _linkedin_partner_id = "${partnerId}";
          window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
          window._linkedin_data_partner_ids.push(_linkedin_partner_id);
          (function(l) {
            if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
            window.lintrk.q=[]}
            var s = document.getElementsByTagName("script")[0];
            var b = document.createElement("script");
            b.type = "text/javascript";b.async = true;
            b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
            s.parentNode.insertBefore(b, s);})(window.lintrk);
        `,
      }}
    />
  );
}

/**
 * Yandex Metrica
 */
interface YandexMetricaProps {
  metricaId: string;
}

export function YandexMetrica({ metricaId }: YandexMetricaProps) {
  if (!metricaId) return null;

  return (
    <Script
      id='yandex-metrica'
      strategy='afterInteractive'
      dangerouslySetInnerHTML={{
        __html: `
          (function(m,e,t,r,i,k,a){m[i]=m[i]||function(){(m[i].a=m[i].a||[]).push(arguments)};
          m[i].l=1*new Date();k=e.createElement(t),a=e.getElementsByTagName(t)[0],k.async=1,k.src=r,a.parentNode.insertBefore(k,a)})
          (window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");
          ym(${metricaId}, "init", {
            clickmap:true,
            trackLinks:true,
            accurateTrackBounce:true,
            webvisor:true
          });
        `,
      }}
    />
  );
}

/**
 * Twitter/X Pixel
 */
interface TwitterPixelProps {
  pixelId: string;
}

export function TwitterPixel({ pixelId }: TwitterPixelProps) {
  if (!pixelId) return null;

  return (
    <Script
      id='twitter-pixel'
      strategy='afterInteractive'
      dangerouslySetInnerHTML={{
        __html: `
          !function(e,t,n,s,u,a){e.twq||(s=e.twq=function(){s.exe?s.exe.apply(s,arguments):s.queue.push(arguments);
          },s.version='1.1',s.queue=[],u=t.createElement(n),u.async=!0,u.src='https://static.ads-twitter.com/uwt.js',
          a=t.getElementsByTagName(n)[0],a.parentNode.insertBefore(u,a))}(window,document,'script');
          twq('config','${pixelId}');
        `,
      }}
    />
  );
}

/**
 * Pinterest Tag
 */
interface PinterestTagProps {
  tagId: string;
}

export function PinterestTag({ tagId }: PinterestTagProps) {
  if (!tagId) return null;

  return (
    <Script
      id='pinterest-tag'
      strategy='afterInteractive'
      dangerouslySetInnerHTML={{
        __html: `
          !function(e){if(!window.pintrk){window.pintrk = function () {
          window.pintrk.queue.push(Array.prototype.slice.call(arguments))};var
          n=window.pintrk;n.queue=[],n.version="3.0";var
          t=document.createElement("script");t.async=!0,t.src=e;var
          r=document.getElementsByTagName("script")[0];
          r.parentNode.insertBefore(t,r)}}("https://s.pinimg.com/ct/core.js");
          pintrk('load', '${tagId}');
          pintrk('page');
        `,
      }}
    />
  );
}

/**
 * TikTok Pixel
 */
interface TikTokPixelProps {
  pixelId: string;
}

export function TikTokPixel({ pixelId }: TikTokPixelProps) {
  if (!pixelId) return null;

  return (
    <Script
      id='tiktok-pixel'
      strategy='afterInteractive'
      dangerouslySetInnerHTML={{
        __html: `
          !function (w, d, t) {
            w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
            ttq.load('${pixelId}');
            ttq.page();
          }(window, document, 'ttq');
        `,
      }}
    />
  );
}

/**
 * Combined Tracking Component
 * Renders all enabled third-party tracking scripts
 */
interface ThirdPartyTrackingProps {
  clarity?: string;
  hotjar?: string;
  facebookPixel?: string;
  linkedInPartner?: string;
  yandexMetrica?: string;
  twitterPixel?: string;
  pinterestTag?: string;
  tiktokPixel?: string;
}

export function ThirdPartyTracking({
  clarity,
  hotjar,
  facebookPixel,
  linkedInPartner,
  yandexMetrica,
  twitterPixel,
  pinterestTag,
  tiktokPixel,
}: ThirdPartyTrackingProps) {
  return (
    <>
      {clarity && <MicrosoftClarity clarityId={clarity} />}
      {hotjar && <Hotjar hotjarId={hotjar} />}
      {facebookPixel && <FacebookPixel pixelId={facebookPixel} />}
      {linkedInPartner && <LinkedInInsight partnerId={linkedInPartner} />}
      {yandexMetrica && <YandexMetrica metricaId={yandexMetrica} />}
      {twitterPixel && <TwitterPixel pixelId={twitterPixel} />}
      {pinterestTag && <PinterestTag tagId={pinterestTag} />}
      {tiktokPixel && <TikTokPixel pixelId={tiktokPixel} />}
    </>
  );
}
